import { EnvType } from "../services";
import ApiClient from "../api-client";
import IRightApiClient from "../Right/right-api-client";
import { GetRightsByTitleIdResponse } from "../Types/get-rights-by-title-id-response";
import { RightType } from "../Types/right-type";
import { MakeUnique } from "../../../Utils/helpers";
import { DbMetadataFieldInfos } from "../Types/db-metadata-field-infos";
import { GetMetadataByTagName } from "../../Utils/asset-object-utils";
import { DbAssetObjectInfos } from "../Types/db-asset-object-infos";
import moment from "moment";
import { DbAccessRightInfos } from "../Types/db-access-right-infos";
import RightApiClient from "../Right/right-api-client";

export interface ContractAndRightType {
  id: number;
  broadcasterName: string;
  contractId?: number;
  customerId: number;
  customerName?: string;
  customerCodeId: string | null;
  startDate: Date | null;
  endDate: Date | null;
  active: boolean;
  externalId: string;
  languages: { key: string; value: string }[];
  mediaNamesString?: string;
  okDelivery: boolean;
  rightLanguagesString?: string;
  rightTerritoriesString?: string;
  territories: { key: string; value: string }[];
  titleId?: number | null;
  titleInfos?: DbAssetObjectInfos;
  titleNbEpisodes?: number;
  titleType?: string;
  type: string;
  workRecordId: string | null;
  dealsNumber: number;
}

export default class RightService {
  rightApiClient: IRightApiClient;

  constructor(envType: EnvType) {
    this.rightApiClient = new RightApiClient(new ApiClient(envType), envType);
  }

  async GetRightContractByTitleIdBasic(itemId: number, type: RightType): Promise<GetRightsByTitleIdResponse> {
    return await this.rightApiClient.GetRightContractByTitleId(itemId, type);
  }
  async GetRightContractByTitleId(
    itemId: number,
    type: RightType,
    metadata,
    unique: boolean
  ): Promise<ContractAndRightType[]> {
    const response = await this.rightApiClient.GetRightContractByTitleId(itemId, type);
    if (response.right === null) return [];
    let result = this.getDealsFromRights([response.right], metadata, unique);
    return result;
  }
  async GetRightContractsByDealExternalId(externalDealId: string, metadata): Promise<ContractAndRightType[]> {
    const response = await this.rightApiClient.GetRightContractsByDealExternalId(externalDealId);
    if (response.rights === null) return [];
    let result = this.getDealsFromRights(response.rights, metadata, false);
    return result;
  }
  async GetRightContractsByCustomerId(customerId: number, metadata, unique: boolean): Promise<ContractAndRightType[]> {
    const response = await this.rightApiClient.GetRightContractsByCustomerId(customerId);
    if (response.rights === null) return [];
    let result = this.getDealsFromRights(response.rights, metadata, unique);
    return result;
  }
  async GetRightContractById(id: number, metadata): Promise<ContractAndRightType[]> {
    const response = await this.rightApiClient.GetRightContractById(id);
    if (response.right === null) return [];
    let result = this.getDealsFromRights([response.right], metadata, false);
    return result;
  }
  async GetRightContracts(maxItems: number, metadata, unique: boolean): Promise<ContractAndRightType[]> {
    const response = await this.rightApiClient.GetRightContracts(maxItems);
    if (response.rights === null) return [];
    let result = this.getDealsFromRights(response.rights, metadata, unique);
    return result;
  }

  //------Utils------
  GetServiceUrl(): string {
    return this.rightApiClient.GetApiUrl();
  }

  contractDealDataProcess = (
    deals: any[] | null,
    titleInfos: DbAssetObjectInfos | null,
    metadataFields: DbMetadataFieldInfos[],
    contractId: number | null = null
  ): ContractAndRightType[] => {
    let data: ContractAndRightType[] = [];
    let index: number = 1;
    let now: any = moment();

    if (!deals) return data;

    // Deals contain rights
    for (let i = 0; i < deals.length; ++i) {
      if (deals[i].rights === null) continue;

      for (let r = 0; r < deals[i].rights.length; ++r) {
        let langsString = "";
        let territoriesString = "";
        let mediaNamesString = "";

        if (deals[i].rights!![r].languageCodes !== null && deals[i].rights[r].languageCodes !== undefined)
          for (let l = 0; l < deals[i].rights[r].languageCodes.length; ++l)
            langsString += " " + deals[i].rights[r].languageCodes![l].key;
        if (deals[i].rights![r].territoryCodes !== null && deals[i].rights[r].territoryCodes !== undefined)
          for (let t = 0; t < deals[i].rights[r].territoryCodes.length; ++t)
            territoriesString += " " + deals[i].rights![r].territoryCodes[t].key;
        for (let m = 0; m < deals[i].rights[r].mediaDetails?.length; ++m) {
          if (deals[i].rights[r].mediaDetails[m].mediaName) {
            mediaNamesString +=
              (mediaNamesString.length > 0 ? ", " : "") + deals[i].rights![r].mediaDetails[m].mediaName;
          }
        }

        let epSeasonTotalNumber;
        if (titleInfos !== undefined && titleInfos !== null)
          epSeasonTotalNumber = GetMetadataByTagName(titleInfos, "serieSeasonEpisodeTotalNumber", metadataFields);

        let row: ContractAndRightType = {
          titleId: titleInfos !== undefined && titleInfos !== null ? titleInfos.id : null,
          titleInfos: titleInfos !== undefined && titleInfos !== null ? titleInfos : undefined,
          customerName: deals[i].customerName,
          customerId: deals[i].customerId,
          customerCodeId: deals[i].customerCodeId,
          broadcasterName: deals[i].broadcasterName,
          startDate: deals[i].rights![r].startDate,
          endDate: deals[i].rights![r].endDate,
          externalId: deals[i].externalId,
          workRecordId: deals[i].workRecordId,
          type: deals[i].rightType,
          rightLanguagesString: langsString,
          rightTerritoriesString: territoriesString,
          mediaNamesString: mediaNamesString,
          okDelivery: deals[i].okDelivery,
          languages: deals[i].rights![r].languageCodes,
          territories: deals[i].rights![r].territoryCodes,
          id: titleInfos?.id ?? 0 + index++,
          //id: 0 + index++,
          titleType: GetMetadataByTagName(titleInfos, "titleType", metadataFields)?.value,
          contractId: contractId ? contractId : 0,
          active: moment(deals[i].rights![r].endDate) > now,
          dealsNumber: deals.length,
          titleNbEpisodes:
            titleInfos !== undefined &&
            titleInfos !== null &&
            epSeasonTotalNumber !== undefined &&
            epSeasonTotalNumber !== null
              ? epSeasonTotalNumber.value
              : null,
        };

        data.push(row);
      }
    }
    return data;
  };

  getDealsFromRights = (rights: DbAccessRightInfos[], metadata: DbMetadataFieldInfos[], unique: boolean) => {
    let dataMapped: ContractAndRightType[] = [];
    if (rights && rights.length > 0)
      for (let i = 0; i < rights.length; ++i) {
        let deals = this.contractDealDataProcess(rights[i].deals, rights[i].titleInfos, metadata, rights[i].id);
        for (let d = 0; d < deals.length; ++d) dataMapped.push(deals[d]);
      }

    if (unique) dataMapped = MakeUnique(dataMapped, ["titleId"]);
    return dataMapped;
  };
}
